/**
 * InlineToast Component
 * 
 * A notification component designed for inline display within content.
 * Features a colored left border and subtle background.
 * 
 * @example
 * ```tsx
 * <InlineToast
 *   id="error-1"
 *   kind="error"
 *   title="Error"
 *   subtitle="Something went wrong"
 *   timestamp={Date.now()}
 *   variant="translucent"
 * />
 * ```
 */
import React from 'react';
import { NotificationBase } from '../../base/NotificationBase';
import type { BaseNotificationProps } from '../../base/types';

export interface InlineToastProps extends Omit<BaseNotificationProps, 'role'> {
  /** Always 'inline' for this component */
  type?: 'inline';
  
  /** Visual variant: translucent (default) or solid (layer1-based) */
  variant?: 'translucent' | 'solid';
}

export const InlineToast: React.FC<InlineToastProps> = ({
  type = 'inline',
  lowContrast = true, // Default to low contrast for inline
  variant = 'translucent',
  ...props
}) => {
  return (
    <NotificationBase
      {...props}
      type="inline"
      lowContrast={lowContrast}
      role="status"
      className={`notification--variant-${variant}`}
    />
  );
};

